/**
 * Helpers PUROS/testáveis pro cockpit Clean Core do Architect.
 *
 * Lêem os TOTAIS do resumo do catálogo WRICEF (`/wricef-catalog/summary`), e
 * não linhas. A Overview recebia as 1.000 primeiras linhas em ordem alfabética
 * e agregava no navegador: medido em 24/09/2026 no `Janela 144 meses [21/09]`,
 * dizia 27 de 1.000 cloud-ready com 620 de 7.288 no catálogo (#922). O banco
 * agrega sobre o catálogo inteiro, e aqui só se dá forma ao que ele devolve.
 *
 * Espelha o padrão de aderenciaData.ts (Functional) — mesma navegação Visão
 * Geral -> Worklist -> Catálogo entre os dois agentes.
 */

import { streamLabel } from "@/lib/wricef-status";
import {
  UNKNOWN_STREAM,
  type CleanCoreLevel,
  type ExtensibilityTarget,
  type WricefCatalogItem,
  type WricefWorklistGroup,
} from "@/types/wricef";

export const CLEAN_CORE_LEVEL_ORDER: CleanCoreLevel[] = ["A", "B", "C", "D"];

/** Contagem por chave, como o agregado a devolve. `_UNKNOWN` é coluna nula. */
type Counts = Record<string, number>;

function isLevel(key: string): key is CleanCoreLevel {
  return key === "A" || key === "B" || key === "C" || key === "D";
}

// ---------------------------------------------------------------------------
// Postura por nível A/B/C/D
// ---------------------------------------------------------------------------

export interface PostureCounts {
  A: number;
  B: number;
  C: number;
  D: number;
  // linhas sem grau clean-core atribuído
  unknown: number;
}

/**
 * Postura A/B/C/D a partir do `by_clean_core_level`. Nível fora do vocabulário
 * conta como sem nota: valor estranho não pode virar avaliação silenciosa.
 */
export function postureFromLevels(byLevel: Counts = {}): PostureCounts {
  const counts: PostureCounts = { A: 0, B: 0, C: 0, D: 0, unknown: 0 };
  for (const [level, n] of Object.entries(byLevel)) {
    if (isLevel(level)) counts[level] += n;
    else counts.unknown += n;
  }
  return counts;
}

function postureTotal(p: PostureCounts): number {
  return p.A + p.B + p.C + p.D + p.unknown;
}

/** % de nível A (cloud-ready) sobre o total, em 0..100 (0 quando vazio). */
export function cloudReadyPct(p: PostureCounts): number {
  const total = postureTotal(p);
  return total === 0 ? 0 : Math.round((p.A / total) * 100);
}

// ---------------------------------------------------------------------------
// Backlog de remediação (C/D)
// ---------------------------------------------------------------------------

export interface AssessmentCoverage {
  /** Linhas com nível A/B/C/D atribuído. */
  assessed: number;
  /** Linhas sem nota: o classificador não chegou nelas. */
  unassessed: number;
  total: number;
  /** Percentual avaliado, 0..100. */
  pct: number;
}

/**
 * Quanto do catálogo chegou a ser classificado.
 *
 * ✅ Medido em 20/08: 68 avaliados e 173 sem nota, num total de 241. Sem este número a
 * tela apresentava "28% cloud-ready" e "0 remediação" lado a lado, e as duas frases
 * juntas diziam que a base está limpa — quando 72% dela nunca foi olhada.
 */
export function assessmentCoverage(p: PostureCounts): AssessmentCoverage {
  const assessed = p.A + p.B + p.C + p.D;
  const total = postureTotal(p);
  return {
    assessed,
    unassessed: p.unknown,
    total,
    pct: total === 0 ? 0 : Math.round((assessed / total) * 100),
  };
}

export interface RemediationBacklog {
  /** C + D: precisa de decisão de extensão, e isso é sabido. */
  confirmed: number;
  /** Sem nota: pode ou não precisar; ninguém avaliou. */
  unassessed: number;
  /** O teto honesto do backlog: confirmado + o que falta avaliar. */
  upperBound: number;
  /** `false` enquanto houver objeto sem nota — o número ainda pode crescer. */
  settled: boolean;
}

/**
 * O backlog de remediação, com a incerteza junto.
 *
 * `confirmed: 0` sozinho lê como "não há trabalho". Com 173 objetos sem nota, o que ele
 * quer dizer é "não há trabalho CONHECIDO", e a diferença entre as duas frases é a
 * proposta inteira.
 */
export function remediationBacklog(p: PostureCounts): RemediationBacklog {
  const confirmed = p.C + p.D;
  return {
    confirmed,
    unassessed: p.unknown,
    upperBound: confirmed + p.unknown,
    settled: p.unknown === 0,
  };
}

// ---------------------------------------------------------------------------
// Densidade por módulo
// ---------------------------------------------------------------------------

export interface ModuleDensity {
  module: string;
  total: number;
  counts: PostureCounts;
  // C + D deste módulo (o que move a ordenação)
  remediation: number;
}

/**
 * Por módulo, a partir do cruzamento `by_stream_clean_core_level`: composição
 * A/B/C/D + remediação (C+D), ORDENADO por remediação desc (empate: total desc,
 * depois nome). Puro/testável.
 *
 * O módulo é o resolvido (#922), o mesmo da coluna do catálogo e do card do
 * Diagnóstico, e o balde sem módulo vai para o fim por maior que seja, com a
 * mesma regra do card: no IDES full-base ele tem 3.939 dos 7.288 objetos, e
 * deixá-lo liderar faria o risco por módulo começar por "não sabemos".
 */
export function moduleBreakdowns(
  byStreamLevel: Record<string, Counts> = {},
  streamLabels?: Record<string, string>,
): ModuleDensity[] {
  const out: ModuleDensity[] = Object.entries(byStreamLevel).map(([stream, byLevel]) => {
    const counts = postureFromLevels(byLevel);
    return {
      module: streamLabel(stream, streamLabels),
      total: postureTotal(counts),
      counts,
      remediation: counts.C + counts.D,
    };
  });
  const unknownLabel = streamLabel(UNKNOWN_STREAM);

  out.sort((a, b) => {
    if (a.module === unknownLabel) return 1;
    if (b.module === unknownLabel) return -1;
    if (b.remediation !== a.remediation) return b.remediation - a.remediation;
    if (b.total !== a.total) return b.total - a.total;
    return a.module.localeCompare(b.module);
  });
  return out;
}

export interface ModuleDensityMap {
  // módulos com remediação relevante (>= minRemediation), em ordem
  hot: ModuleDensity[];
  // resto colapsado numa linha "Outras N módulos"
  rest: ModuleDensity[];
}

/**
 * Separa os módulos "quentes" (>= minRemediation de C+D) dos demais, que
 * colapsam numa linha resumo. Default minRemediation=1 (módulos sem remediação
 * colapsam).
 */
export function splitModuleMap(
  breakdowns: ModuleDensity[],
  minRemediation = 1,
): ModuleDensityMap {
  const hot: ModuleDensity[] = [];
  const rest: ModuleDensity[] = [];
  for (const b of breakdowns) {
    if (b.remediation >= minRemediation) hot.push(b);
    else rest.push(b);
  }
  // Sem nenhum C/D o critério não separa nada e TODO módulo colapsa: a tela mostrava
  // "Other 11 modules · 241 objects" numa linha só, com a composição de BC (85),
  // HR/HCM (60), FI (34) e SD (32) somindo. ✅ Medido em 20/08. Quando não há
  // remediação para ordenar, o volume é o critério que sobra — e é melhor que nenhum.
  //
  // O balde sem módulo continua no fim também aqui: na passada profunda do
  // `E2E Test` ele é 239 de 241 e, ordenado só por volume, encabeçava a lista.
  if (hot.length === 0 && rest.length > 0) {
    const unknownLabel = streamLabel(UNKNOWN_STREAM);
    const byVolume = [...rest].sort((a, b) => {
      if (a.module === unknownLabel) return 1;
      if (b.module === unknownLabel) return -1;
      return b.total - a.total;
    });
    return { hot: byVolume.slice(0, 8), rest: byVolume.slice(8) };
  }
  return { hot, rest };
}

/**
 * Agrega densidade por módulo no formato do mockup: módulos quentes em ordem
 * de remediação + uma linha colapsada "Outras N módulos". Atalho sobre
 * moduleBreakdowns + splitModuleMap.
 */
export function moduleDensity(
  byStreamLevel: Record<string, Counts> = {},
  streamLabels?: Record<string, string>,
): ModuleDensityMap {
  return splitModuleMap(moduleBreakdowns(byStreamLevel, streamLabels));
}

// ---------------------------------------------------------------------------
// Estratégia de extensão (extensibility_target)
// ---------------------------------------------------------------------------

export type ExtensibilityCounts = Record<ExtensibilityTarget, number> & {
  // linhas sem alvo de extensão atribuído
  unknown: number;
};

export const EXTENSIBILITY_ORDER: ExtensibilityTarget[] = [
  "key_user",
  "side_by_side",
  "developer",
];

/** Contagens por extensibility_target (key_user/side_by_side/developer) + o resto. */
export function extensibilitySplit(byTarget: Counts = {}): ExtensibilityCounts {
  const counts: ExtensibilityCounts = {
    key_user: 0,
    side_by_side: 0,
    developer: 0,
    unknown: 0,
  };
  for (const [target, n] of Object.entries(byTarget)) {
    if (target === "key_user" || target === "side_by_side" || target === "developer") {
      counts[target] += n;
    } else {
      counts.unknown += n;
    }
  }
  return counts;
}

// ---------------------------------------------------------------------------
// Split de conversão (conversion_tipo)
// ---------------------------------------------------------------------------

export interface ConversionEntry {
  tipo: string;
  count: number;
}

/**
 * Contagens por conversion_tipo, ORDENADO por count desc (empate: nome). O balde
 * sem tipo fica de fora: na passada leve ele é o catálogo inteiro. Puro/testável.
 */
export function conversionSplit(byType: Counts = {}): ConversionEntry[] {
  return Object.entries(byType)
    // `_UNKNOWN` é o mesmo sentinela do agregado para coluna nula.
    .filter(([tipo, count]) => tipo !== "_UNKNOWN" && count > 0)
    .map(([tipo, count]) => ({ tipo, count }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.tipo.localeCompare(b.tipo);
    });
}

// ---------------------------------------------------------------------------
// Esforço total
// ---------------------------------------------------------------------------

export interface EffortSplit {
  /** Horas em objetos que receberam nota de clean core. */
  assessed: number;
  /** Horas em objetos que ninguém classificou. */
  unassessed: number;
  total: number;
}

/**
 * O esforço total, separado pelo que foi avaliado, a partir do
 * `effort_h_by_clean_core_level`.
 *
 * ✅ Medido em 20/08: 19.556h no catálogo inteiro, das quais só 5.582h vêm dos 68
 * objetos com nota. Anunciar o total sem essa separação apresenta como estimativa
 * calibrada um número em que 72% do peso vem de objeto que nunca foi olhado.
 */
export function effortSplit(effortByLevel: Counts = {}): EffortSplit {
  let assessed = 0;
  let unassessed = 0;
  for (const [level, hours] of Object.entries(effortByLevel)) {
    if (isLevel(level)) assessed += hours;
    else unassessed += hours;
  }
  return { assessed, unassessed, total: assessed + unassessed };
}

// ---------------------------------------------------------------------------
// Worklist: a página do banco, fatiada por módulo
// ---------------------------------------------------------------------------

export interface WorklistSection {
  /** Módulo resolvido, ou `UNKNOWN_STREAM`. */
  stream: string;
  /** Tamanho do grupo na worklist inteira, e não o pedaço desta página. */
  total: number;
  remediation: number;
  /** O grupo começou numa página anterior. */
  continued: boolean;
  items: WricefCatalogItem[];
}

/**
 * Fatia uma página da worklist nas seções de módulo que ela atravessa.
 *
 * Quem decide o que é worklist e em que ordem é o banco
 * (`/wricef-catalog/worklist`): C/D ou criticidade alta, grupos por densidade de
 * remediação com o sem módulo no fim, criticidade dentro do grupo. Aqui só se
 * corta o que chegou. `groups` vem na mesma ordem da paginação, e `offset` diz
 * onde a página começa nela; com filtro, a ordem conta só os grupos filtrados,
 * como no banco.
 */
export function worklistSections(
  items: WricefCatalogItem[],
  groups: WricefWorklistGroup[],
  offset: number,
  selected: Set<string> = new Set(),
): WorklistSection[] {
  const visible = selected.size ? groups.filter((g) => selected.has(g.stream)) : groups;
  const startOf = new Map<string, number>();
  let cursor = 0;
  for (const g of visible) {
    startOf.set(g.stream, cursor);
    cursor += g.count;
  }
  const meta = new Map(groups.map((g) => [g.stream, g]));

  const sections: WorklistSection[] = [];
  for (const it of items) {
    const stream = it.resolved_stream ?? UNKNOWN_STREAM;
    const last = sections[sections.length - 1];
    if (last && last.stream === stream) {
      last.items.push(it);
      continue;
    }
    const g = meta.get(stream);
    sections.push({
      stream,
      total: g?.count ?? 0,
      remediation: g?.remediation ?? 0,
      continued: sections.length === 0 && (startOf.get(stream) ?? offset) < offset,
      items: [it],
    });
  }
  return sections;
}
